import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import Login from './components/Login'
import { useTranslation } from 'react-i18next'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { useAgentStore } from './store/useAgentStore'

import Layout from './components/Layout'
import Dashboard from './components/Dashboard'
import HealthMonitor from './components/HealthMonitor'
import AlertsPanel from './components/AlertsPanel'
import SettingsPanel from './components/SettingsPanel'
import MemoryGallery from './components/MemoryGallery'
import ChatSimulator from './components/ChatSimulator'
import MedicationsPanel from './components/MedicationsPanel'

function RequireAuth({ children }: { children: JSX.Element }) {
  const token = useAgentStore((s) => s.accessToken)
  if (!token) return <Navigate to="/login" replace />
  return children
}

function App() {
  const { i18n, t } = useTranslation()
  const { language, isDarkMode } = useAgentStore()

  // PWA: service worker registration + update prompt
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW()

  // Sync language with store
  useEffect(() => {
    if (language && language !== i18n.language) {
      i18n.changeLanguage(language)
    }
  }, [language, i18n])

  // Apply dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [isDarkMode])

  // Apply RTL
  useEffect(() => {
    const isRTL = ['ar', 'he', 'ur'].includes(i18n.language)
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
  }, [i18n.language])

  return (
    <>
      {(offlineReady || needRefresh) && (
        <div className="fixed bottom-4 inset-x-4 z-50 card flex items-center justify-between gap-4 shadow-lg">
          <div className="flex items-center gap-3">
            <span className="text-lg">
              {needRefresh ? t('pwa.update_available') : t('pwa.offline_ready')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {needRefresh && (
              <button
                className="btn-primary !px-4 !py-2 !text-base"
                onClick={() => updateServiceWorker(true)}
              >
                {t('pwa.reload')}
              </button>
            )}
            <button
              className="btn-primary !px-4 !py-2 !text-base bg-gray-400 hover:bg-gray-500"
              onClick={() => {
                setOfflineReady(false)
                setNeedRefresh(false)
              }}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="health" element={<HealthMonitor />} />
          <Route path="medications" element={<MedicationsPanel />} />
          <Route path="alerts" element={<AlertsPanel />} />
          <Route path="settings" element={<SettingsPanel />} />
          <Route path="memories" element={<MemoryGallery />} />
          <Route path="chat" element={<ChatSimulator />} />
        </Route>
    <Route path="*" element={
        <div className="flex flex-col items-center justify-center h-screen">
            <h1 className="text-4xl font-bold text-gray-800">404</h1>
            <p className="text-gray-600 mt-2">Page not found</p>
            <a href="/" className="mt-4 text-blue-600 hover:underline">Go home</a>
        </div>
    } />
    </Routes>
    </>
  )
}

export default App